/**
 * @packageDocumentation ButtonMenu
 *
 * Renders a button group component.
 *
 * Example
 * ```tsx
 * import ButtonMenu, { ButtonMenuItem } from '~/components/buttons/ButtonMenu';
 *
 * ...
 *
 * <ButtonMenu label="download">
 *  <ButtonMenu.Item onAction={doSomething} title="Action 1" />
 *  <ButtonMenu.Item onAction={doSomethingElse} title="Action 2" />
 * </ButtonMenuI>
 * ```
 * @category Component
 */

import { mergeRefs } from '@react-aria/utils';
import clsx from 'clsx';
import React, { PropsWithChildren, useRef, useState } from 'react';
import { Menu, MenuItem, MenuItemProps, MenuTrigger, Popover } from 'react-aria-components';
import CircleSpinner from '~/components/data-display/CircleSpinner/CircleSpinner';
import Text from '~/components/typographies/Text';
import useDataTestId from '~/hooks/useDataTestId';
import { isDefined } from '~/types/typeGuards';
import styles from './ButtonMenu.module.scss';
import Button, { ButtonProps } from './NewButton';
import IconButton from './NewIconButton';

/**
 * The props type for {@link ButtonMenu.Item}.
 * Extends {@link MenuItemProps}.
 *
 * @interface
 * @category Props
 */
type ButtonMenuItemProps = Omit<MenuItemProps, 'children'> & {
  action?: 'destructive';
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  loading?: boolean;
  dataTestId?: string;
  title: string;
  subtitle?: string;
};

/**
 * Renders a button group item that can be passed as children of {@link ButtonMenu} component.
 * If the button is disabled or in loading state, user cannot interact with it.
 *
 * Example with text children
 * ```tsx
 * <ButtonMenu.Item onAction={doSomething} title="Some item text" />
 *
 * @category Component
 */
const ButtonMenuItem = React.forwardRef<HTMLDivElement, ButtonMenuItemProps>(
  ({ Icon, loading, dataTestId, isDisabled, title, subtitle, action, ...props }, ref) => {
    const dt = useDataTestId(dataTestId);
    return (
      <MenuItem
        {...props}
        className={clsx(styles.menuItem, props.className, {
          [styles.loading!]: loading,
          [styles.destructive!]: action === 'destructive',
        })}
        ref={ref}
        isDisabled={loading || isDisabled}
        data-testid={dt()}
      >
        {Icon && <Icon className={styles.menuItemIcon} />}
        <div className={styles.menuItemChildren} data-testid={dt('children')}>
          <Text variant={'body2'} weight={'regular'}>
            {title}
          </Text>
          {isDefined(subtitle) && (
            <Text variant={'caption'} weight={'medium'} secondaryColor>
              {subtitle}
            </Text>
          )}
        </div>
        {loading ? <CircleSpinner size="small" className={styles.menuItemSpinner} dataTestId={dt('spinner')} /> : null}
      </MenuItem>
    );
  },
);

/**
 * The props type for {@link ButtonMenu}.
 * Extends {@link ButtonProps}.
 *
 * @interface
 * @property label Button label. Can be a string or a JSX element.
 * @category Props
 */
export type ButtonMenuProps = Omit<
  ButtonProps,
  'onClick' | 'onPressStart' | 'href' | 'type' | 'data-testid' | 'icon' | 'variant'
> & {
  popoverAlignment?: 'left' | 'right';
  popoverOffset?: number;
  dataTestId?: string;
  dataIntercomTarget?: string;
  variant?: 'outlined' | 'ghost';
  ref?: React.Ref<HTMLElement>;
} & (
    | { Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>; iconLabel?: string }
    | {
        label: string;
        Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
      }
  );

const ButtonMenu: React.FC<PropsWithChildren<ButtonMenuProps>> & { Item: React.FC<ButtonMenuItemProps> } = ({
  popoverAlignment = 'right',
  popoverOffset,
  children,
  dataTestId,
  ref: forwardedRef,
  dataIntercomTarget,
  ...props
}) => {
  const dt = useDataTestId(dataTestId);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const internalButtonRef = useRef<HTMLElement>(null);
  const buttonRef = mergeRefs(forwardedRef, internalButtonRef);

  const [crossOffset, setCrossOffset] = useState(0);

  return (
    <MenuTrigger>
      {'label' in props ? (
        <Button {...props} ref={buttonRef} data-testid={dt('button')} data-intercom-target={dataIntercomTarget}>
          {props.label}
        </Button>
      ) : (
        <IconButton
          {...props}
          ref={buttonRef}
          data-testid={dt('button')}
          label={props.iconLabel}
          data-intercom-target={dataIntercomTarget}
        />
      )}
      <Popover
        crossOffset={popoverAlignment === 'right' ? crossOffset : 0}
        offset={popoverOffset}
        data-testid={dt('popover')}
      >
        <Menu
          ref={(el) => {
            menuRef.current = el ?? null;
            const crossOffset = (internalButtonRef.current?.clientWidth ?? 0) - (el?.clientWidth ?? 0);
            setCrossOffset(crossOffset < 0 ? crossOffset : 0);
          }}
          className={styles.menu}
          data-testid={dt('menu')}
        >
          {children}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};

ButtonMenu.Item = ButtonMenuItem;

export default ButtonMenu;
